import { z } from 'zod';

import { MiceGenderSchema } from './mice_profile';
import { NumericIdSchema } from './mice_refs';

// Folha (só importa `mice_profile` e `mice_refs`): `mice` e `mice_invites` dependem dela sem fechar ciclo.

/** Regra de quartos do evento: só mesmo gênero ou livre. */
export const MiceRoomGenderRuleSchema = z.enum(['same_gender', 'any']);
export type MiceRoomGenderRule = z.infer<typeof MiceRoomGenderRuleSchema>;

/** Como a dupla nasceu: escolha mútua ou o organizador pareou à mão. */
export const MiceRoomPairSourceSchema = z.enum(['mutual', 'organizer']);
export type MiceRoomPairSource = z.infer<typeof MiceRoomPairSourceSchema>;

/** Por que a pessoa ainda está sem dupla. */
export const MiceRoomUnpairedReasonSchema = z.enum([
  'no_pick',
  'pick_pending',
  'pick_elsewhere',
  'partner_left',
  'gender_missing',
]);
export type MiceRoomUnpairedReason = z.infer<typeof MiceRoomUnpairedReasonSchema>;

export const MiceRoomPersonSchema = z.object({
  participantId: z.string(),
  name: z.string(),
  gender: MiceGenderSchema.nullable(),
});
export type MiceRoomPerson = z.infer<typeof MiceRoomPersonSchema>;

export const MiceRoomPairViewSchema = z.object({
  id: z.string(),
  members: z.tuple([MiceRoomPersonSchema, MiceRoomPersonSchema]),
  source: MiceRoomPairSourceSchema,
  /** A dupla fere a regra de gênero (só o organizador consegue formá-la). */
  ruleConflict: z.boolean(),
});
export type MiceRoomPairView = z.infer<typeof MiceRoomPairViewSchema>;

export const MiceRoomUnpairedSchema = z.object({
  person: MiceRoomPersonSchema,
  reason: MiceRoomUnpairedReasonSchema,
  /** Nome de quem a pessoa escolheu; nulo sem escolha. */
  pickName: z.string().nullable(),
});
export type MiceRoomUnpaired = z.infer<typeof MiceRoomUnpairedSchema>;

export const MiceRoomsViewSchema = z.object({
  genderRule: MiceRoomGenderRuleSchema,
  /** Quando o organizador fechou os quartos; nulo = ainda abertos. */
  closedAt: z.iso.datetime().nullable(),
  pairs: z.array(MiceRoomPairViewSchema),
  unpaired: z.array(MiceRoomUnpairedSchema),
});
export type MiceRoomsView = z.infer<typeof MiceRoomsViewSchema>;

/** Resposta das rotas de quartos do organizador. */
export const MiceRoomsReadSchema = z.object({ rooms: MiceRoomsViewSchema });
export type MiceRoomsRead = z.infer<typeof MiceRoomsReadSchema>;

/** Resumo na ficha do evento (`MiceProjectView.rooms`). */
export const MiceRoomsSummarySchema = z.object({
  pairs: z.number().int().min(0),
  unpaired: z.number().int().min(0),
  closedAt: z.iso.datetime().nullable(),
});
export type MiceRoomsSummary = z.infer<typeof MiceRoomsSummarySchema>;

/** Onde o convidado está no passo de quartos do convite. */
export const MiceInviteRoomStateSchema = z.enum(['choosing', 'paired', 'organizer', 'closed']);
export type MiceInviteRoomState = z.infer<typeof MiceInviteRoomStateSchema>;

export const MiceInviteRoomSchema = z.object({
  genderRule: MiceRoomGenderRuleSchema,
  /** A regra pede gênero e o perfil do convidado ainda não tem. */
  needsGender: z.boolean(),
  /** O sexo do PRÓPRIO convidado que a regra `same_gender` usa (nulo sem regra ou sem sexo): o
   *  passo o mostra com "Alterar" enquanto o quarto está em escolha. Default: view de um mice-api
   *  antigo. */
  myGender: MiceGenderSchema.nullable().default(null),
  state: MiceInviteRoomStateSchema,
  pick: z.object({ participantId: z.string(), name: z.string() }).nullable(),
  /** Nome do colega com quem já está pareado. */
  pairedWith: z.string().nullable(),
  candidates: z.array(
    z.object({ participantId: z.string(), name: z.string(), taken: z.boolean() }),
  ),
});
export type MiceInviteRoom = z.infer<typeof MiceInviteRoomSchema>;

export const MiceInviteRoomPickRequestSchema = z.object({
  travelerId: NumericIdSchema,
  /** Nulo desfaz a escolha. */
  participantId: z.string().nullable(),
});
export type MiceInviteRoomPickRequest = z.infer<typeof MiceInviteRoomPickRequestSchema>;

export const MiceInviteRoomGenderRequestSchema = z.object({
  travelerId: NumericIdSchema,
  gender: MiceGenderSchema,
});
export type MiceInviteRoomGenderRequest = z.infer<typeof MiceInviteRoomGenderRequestSchema>;

/** O que a troca do sexo desfez (`POST .../room/gender`): a escolha de colega que deixou de
 *  seguir a regra `same_gender`. */
export const MiceInviteRoomChangeSchema = z.object({
  undonePick: z.object({ participantId: z.string(), name: z.string() }).nullable(),
});
export type MiceInviteRoomChange = z.infer<typeof MiceInviteRoomChangeSchema>;

export const MiceRoomPairRequestSchema = z.object({
  organizerId: NumericIdSchema,
  participantIds: z.tuple([z.string(), z.string()]).refine(([a, b]) => a !== b, {
    message: 'participantIds must be two different participants',
  }),
  /** O organizador confirma parear contra a regra de gênero. */
  confirmRuleConflict: z.boolean().default(false),
});
export type MiceRoomPairRequest = z.infer<typeof MiceRoomPairRequestSchema>;

export const MiceRoomsCloseRequestSchema = z.object({
  organizerId: NumericIdSchema,
  closed: z.boolean(),
});
export type MiceRoomsCloseRequest = z.infer<typeof MiceRoomsCloseRequestSchema>;
